import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { CubeIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import { logAnalyticsEvent } from "../lib/firebase";

const GameModes: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const modes = [
    {
      title: "対戦モード",
      description:
        "親子関係のある本格的な対戦を楽しもう。特殊役で最大10倍の配当も！",
      icon: CubeIcon,
      features: [
        "１人で楽しめる",
        "ベッティングシステム搭載",
        "特殊役ボーナス",
      ],
      preview: "/images/battle_preview.png",
    },
    {
      title: "フリーモード",
      description: "友達と気軽に遊べる練習モード。複数人での対戦も可能です。",
      icon: UserGroupIcon,
      features: ["１人でも複数人でも", "親番・順番調整", "カスタマイズ"],
      preview: "/images/free_preview.png",
    },
  ];

  const handleModeClick = (modeName: string) => {
    logAnalyticsEvent("game_mode_view", {
      mode_name: modeName,
    });
  };

  return (
    <section id="modes" className="py-16">
      <div className="container max-w-5xl">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title text-center mb-12">ゲームモード</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {modes.map((mode, index) => (
              <motion.div
                key={mode.title}
                className="card relative overflow-hidden group h-full"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                onClick={() => handleModeClick(mode.title)}
              >
                <div className="flex flex-col h-full">
                  <div className="flex items-center mb-3">
                    <mode.icon className="w-8 h-8 text-accent mr-3" />
                    <h3 className="text-xl font-bold">{mode.title}</h3>
                  </div>
                  <p className="text-gray-300 text-sm mb-4">
                    {mode.description}
                  </p>
                  <ul className="space-y-1.5 mb-4 text-sm">
                    {mode.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center text-gray-300"
                      >
                        <svg
                          className="w-4 h-4 text-accent mr-2 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <motion.div
                    className="relative rounded-lg overflow-hidden shadow-lg mt-auto"
                    whileHover={{ scale: 1.03 }}
                    transition={{ duration: 0.2 }}
                  >
                    <img
                      src={mode.preview}
                      alt={`${mode.title}のプレビュー`}
                      className="w-full h-auto rounded-lg"
                      width="800"
                      height="600"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GameModes;
